import i18n from "i18next";
import { omit } from "lodash";
import { batch } from "react-redux";
import unkinkPolygon from "@turf/unkink-polygon";
import shapeToGeoJson from "../../pages/Map/shapeToGeoJson";
import * as api from "../../api/recipientLists";
import { notify } from "../../actions/notification";
import {
  filterValuesBySearchTerms,
  sortValuesAlphabetically,
} from "../../utils/masterListSearchTerms";
import {
  buildNewRecipientList,
  clearNewRecipientList,
  getMasterListHeadersAction,
} from "./index";

export function getMasterListHeaders() {
  return async (dispatch, getState) => {
    const { storage } = getState();
    const { activeDivisionId } = storage;

    const masterLists = await api.listMasterLists({ activeDivisionId });
    const firstMasterList = masterLists.data[0] || {};
    
    // Extract all important fields including count data
    const {
      _id = null,
      recipientHeaders = {},
      totalCount = 0,
      failedCount = 0,
      emailCount = 0,
      smsCount = 0,
      voiceCount = 0,
      ttyCount = 0,
      customCount = 0
    } = firstMasterList;

    dispatch(
      getMasterListHeadersAction({ recipientHeaders, recipientListId: _id })
    );

    dispatch(
      buildNewRecipientList({
        recipientHeaders,
        masterListId: _id,
        recipientFilters: {},
        totalCount,
        failedCount,
        emailCount,
        smsCount,
        voiceCount,
        ttyCount,
        customCount
      })
    );
  };
}

export function getMasterListRecipientHeaders() {
  return async (dispatch, getState) => {
    const { storage } = getState();
    const { activeDivisionId } = storage;

    const masterLists = await api.listMasterLists({ activeDivisionId });
    const firstMasterList = masterLists.data[0] || {};

    // Extract required fields
    const {
      _id = null,
      recipientHeaders = {},
      totalCount = 0,
      failedCount = 0,
      emailCount = 0,
      smsCount = 0,
      voiceCount = 0,
      ttyCount = 0,
      customCount = 0
    } = firstMasterList;

    dispatch(
      getMasterListHeadersAction({ recipientHeaders, recipientListId: _id })
    );

    batch(() => {
      dispatch(buildNewRecipientList({
        masterListId: _id,
        totalCount,
        failedCount,
        emailCount,
        smsCount,
        voiceCount,
        ttyCount,
        customCount
      }));
    });
  };
}

export function toggleFilterColumn(header, { allowEmpty = true } = {}) {
  return (dispatch, getState) => {
    const { newRecipientList } = getState() || {};
    const { recipientFilters = {}, errors = {} } = newRecipientList;

    if (recipientFilters[header]) {
      // redirect the user to the first step and display an error message
      // if this was the last turned on header and allowEmpty is false
      if (!allowEmpty && Object.keys(recipientFilters).length <= 1) {
        dispatch(
          buildNewRecipientList({
            doneStepIdx: -1,
            recipientFilters: {},
            errors: {},
          })
        );
        dispatch(
          notify({
            type: "error",
            message: i18n.t(
              "You must select at least one column to filter on."
            ),
          })
        );
      } else {
        dispatch(
          buildNewRecipientList({
            recipientFilters: omit(recipientFilters, header),
            errors: omit(errors, header),
          })
        );
      }
    } else {
      dispatch(
        buildNewRecipientList({
          recipientFilters: { ...recipientFilters, [header]: {} },
          errors: omit(errors, "message", header),
        })
      );
    }
  };
}

export function validateFilterColumns() {
  return (dispatch, getState) => {
    const { recipientFilters = {} } = getState().newRecipientList;

    if (!Object.keys(recipientFilters).length) {
      dispatch(
        buildNewRecipientList({
          doneStepIdx: -1,
          errors: {
            message: i18n.t(
              "You must select at least one column to filter on."
            ),
          },
        })
      );
      return false;
    }

    dispatch(
      buildNewRecipientList({
        doneStepIdx: 0,
        errors: {},
      })
    );
    return true;
  };
}

export function getDistinctMasterListColumnValues({ header, search }) {
  return async (dispatch, getState) => {
    const { masterListId } = getState().newRecipientList;

    const { data: values } = await api.listMasterRecipients({
      recipientListId: masterListId,
      distinct: true,
      searchBy: header,
      search,
      page: 0,
      itemsPerPage: 50,
    });

    // this has to be retrieved here again because of the async for part of the function
    const { recipientFilters } = getState().newRecipientList;
    dispatch(
      buildNewRecipientList({
        recipientFilters: {
          ...recipientFilters,
          [header]: {
            values,
            selectedValues: recipientFilters[header]?.selectedValues ?? [],
          },
        },
      })
    );
  };
}

export function getMasterListHeaderFields({ search, header }) {
  return async (dispatch, getState) => {
    const { storage } = getState();
    const { paid } = storage;

    if (process.env.USE_DEPRECIATED_MASTER_LIST_HEADER) {
      return dispatch(getDistinctMasterListColumnValues({ header, search }));
    }

    const { masterListId, recipientFilters = {} } = getState().newRecipientList;

    let newRecipientFilters = recipientFilters;

    if (search) {
      const item = newRecipientFilters[header];
      const filteredValues = sortValuesAlphabetically(
        filterValuesBySearchTerms(item?.allValues, search)
      );
      newRecipientFilters[header].values = filteredValues;
    } else {
      const values = sortValuesAlphabetically(
        await api.listMasterListHeaderFields({
          masterListId,
          header,
          paid,
        })
      );
      // this has to be retrieved here again because of the async for part of the function
      const { recipientFilters } = getState().newRecipientList;

      newRecipientFilters = {
        ...recipientFilters,
        [header]: {
          values,
          selectedValues: newRecipientFilters[header].selectedValues ?? [],
          allValues: values,
        },
      };
    }

    dispatch(
      buildNewRecipientList({
        recipientFilters: newRecipientFilters,
      })
    );
  };
}

export function toggleFilterValue(header, value) {
  return (dispatch, getState) => {
    const { recipientFilters = {}, errors = {} } = getState().newRecipientList;
    const { selectedValues = [] } = recipientFilters[header] || {};

    const valueIdx = selectedValues.indexOf(value);
    let newSelectedValues;

    if (valueIdx === -1) {
      newSelectedValues = [...selectedValues, value];
    } else {
      newSelectedValues = selectedValues.filter((v) => v !== value);
    }

    dispatch(
      buildNewRecipientList({
        errors: omit(errors, header),
        recipientFilters: {
          ...recipientFilters,
          [header]: {
            ...recipientFilters[header],
            selectedValues: newSelectedValues,
          },
        },
      })
    );
  };
}

export function validateFilterValues() {
  return (dispatch, getState) => {
    const { newRecipientList } = getState();
    const { recipientFilters = {}, errors = {} } = newRecipientList;

    for (const header in recipientFilters) {
      // Handle case when recipientFilters[header] might be undefined
      const { selectedValues = [] } = recipientFilters[header] || {};
      if (!selectedValues.length) {
        errors[header] = i18n.t(
          "Select at least one value or remove this column.",
          {
            header,
          }
        );
      }
    }

    if (Object.keys(errors).length) {
      dispatch(buildNewRecipientList({ errors, doneStepIdx: 0 }));
      return false;
    }

    dispatch(buildNewRecipientList({ errors: {}, doneStepIdx: 1 }));
    return true;
  };
}

export function validateMapPolygon() {
  return (dispatch, getState) => {
    const { shapes = [], enabledLayers = {} } = getState().map;
    const { 
      totalCount = 0,
      failedCount = 0,
      emailCount = 0,
      smsCount = 0,
      voiceCount = 0,
      ttyCount = 0,
      customCount = 0
    } = getState().newRecipientList;
    
    const enabledLayerKeys = Object.keys(enabledLayers);

    if (!shapes.length && !enabledLayerKeys.length) {
      dispatch(
        buildNewRecipientList({
          errors: { message: i18n.t("You must select or draw some polygons") },
          doneStepIdx: -1,
          loading: false
        })
      );
      return false;
    }

    const drawnFeatures = shapes.map((shape) => shapeToGeoJson(shape));
    const layerFeatures = enabledLayerKeys.reduce((features, layerKey) => {
      const { layer } = enabledLayers[layerKey];
      return [...features, ...(layer?.data?.features ?? [])];
    }, []);

    // mongodb does not understand self intersecting polygons
    // this function splits these polygons into 'normal' ones
    // https://turfjs.org/docs/#unkinkPolygon
    const withinCollection = unkinkPolygon({
      type: "FeatureCollection",
      features: [...drawnFeatures, ...layerFeatures],
    });

    const withinPolygon = {
      type: "Feature",
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          ...withinCollection.features.map(
            (feature) => feature.geometry.coordinates
          ),
        ],
      },
    };

    dispatch(
      buildNewRecipientList({ 
        errors: {}, 
        withinPolygon, 
        doneStepIdx: 0,
        loading: false,
        totalCount,
        failedCount,
        emailCount,
        smsCount,
        voiceCount,
        ttyCount,
        customCount
      })
    );
    return true;
  };
}

export function filterMasterRecipients() {
  return async (dispatch, getState) => {
    const { newRecipientList } = getState();
    const { _id: recipientListId, recipientFilters = {}, masterListId, name } = newRecipientList;
    // transform the filters into a { prop: [value1, value2, ...] } format

    try {
      const filters = Object.keys(recipientFilters).reduce(
        (acc, prop) => ({
          ...acc,
          [prop]: recipientFilters[prop]?.selectedValues || [],
        }),
        {}
      );

      if (!masterListId) {
        throw new Error("Master list ID is required");
      }

      const response = await api.filterMasterRecipients({
        recipientListId,
        masterListId,
        filters,
      });

      const {
        totalCount = 0,
        failedCount = 0,
        emailCount = 0,
        smsCount = 0,
        voiceCount = 0,
        ttyCount = 0,
        customCount = 0
      } = response.data || {};

      batch(() => {
        dispatch(buildNewRecipientList({ 
          loading: false,
          totalCount,
          failedCount,
          emailCount,
          smsCount,
          voiceCount,
          ttyCount,
          customCount
        }));
        
        dispatch(
          notify({
            type: "success",
            message: i18n.t("Successfully uploaded {{name}}!", { name }),
          })
        );
      });
    } catch (error) {
      batch(() => {
        dispatch(buildNewRecipientList({ loading: false }));
        dispatch(
          notify({
            type: "error", 
            message: error.response?.data?.message || "Failed to filter recipients"
          })
        );
      });
      throw error;
    }
  };
}

export function geoFilterMasterRecipients() {
  return async (dispatch, getState) => {
    const { newRecipientList } = getState();
    const { 
      _id: recipientListId, 
      withinPolygon,
      masterListId,
      name 
    } = newRecipientList;

    if (!recipientListId || !withinPolygon) {
      dispatch(buildNewRecipientList({
        loading: false,
        errors: { general: 'Missing required data for geo filtering' }
      }));
      dispatch(
        notify({
          type: "error",
          message: i18n.t("Missing required data for geo filtering")
        })
      );
      return;
    }

    try {

      if (!masterListId) {
        throw new Error("Master list ID is required");
      }

      const response = await api.filterMasterRecipients({
        recipientListId,
        masterListId,
        withinPolygon,
        filters: {}
      });

      const {
        totalCount = 0,
        failedCount = 0,
        emailCount = 0,
        smsCount = 0,
        voiceCount = 0,
        ttyCount = 0,
        customCount = 0
      } = response.data || {};

      batch(() => {
        dispatch(buildNewRecipientList({ 
          loading: false,
          totalCount,
          failedCount,
          emailCount,
          smsCount,
          voiceCount,
          ttyCount,
          customCount
        }));
        
        dispatch(
          notify({
            type: "success",
            message: i18n.t("Successfully uploaded {{name}}!", { name }),
          })
        );
      });
    } catch (error) {
      batch(() => {
        dispatch(buildNewRecipientList({ loading: false }));
        dispatch(
          notify({
            type: "error",
            message: i18n.t("Failed to filter recipients: {{error}}", {
              error: error?.response?.data?.message || error.message
            }),
          })
        );
      });
      throw error;
    }
  };
}
